/**
   * An improved implementation of the https://github.com/eriktufvesson/ngBootbox package.
   * The standard implementation does not implement sanitization using the $sce provider.
   * The methods take either an options object which is extended with templateHtml/templateUrl and scope properties.
   * When using these options the message is not sanitized. When using the raw bootbox message value we use $sce.getTrustedHtml to
   * sanitize the provided message. This still allows for HTML (styling) in the code but it does not bootstrap the content for using 
   * angular bindings and scope.
   * Additionally the service wraps the bootbox invocation in promises. This allows us to simply chain multiple dialogs.
   *  
   * For example when requiring confirmation before requiring a reason/motivation can look like
   * 
   * @example 
   * habilBootbox.confirm("Are you sure you want to do X?")
   *    .then(onConfirm)
   *    .then(doActionX)
   *    .catch(handleErrors);
   * 
   * function onConfirm() {
   *    // ...
   *    return habilBootbox.prompt(options);
   * }
   */
(function () {
    'use strict';

    angular.module('app')
        .provider('$habilBootboxConfig', ConfigProvider)
        .directive('habilBootboxAlert', ["habilBootbox", AlertDirective])
        .directive('habilBootboxConfirm', ["habilBootbox", ConfirmDirective])
        .directive('habilBootboxPrompt', ["habilBootbox", PromptDirective])
        .directive('habilBootboxCustomDialog', ["habilBootbox", CustomDialogDirective])
        .directive('habilBootboxCloseDialog', ["habilBootbox", CloseDialogDirective])
        .factory('habilBootbox', ["$q", "$templateCache", "$compile", "$rootScope", "$http", "$window", "$sce", "translationsService", HabilBootboxService]);

    function ConfigProvider() {
        let defaultLocale = '';

        return {
            // We cannot use $window in a config block
            /* eslint-disable angular/window-service */
            setDefaultLocale: function (name) {
                defaultLocale = name;
                window.bootbox.setLocale(name);
            },
            addLocale: function (name, values) {
                window.bootbox.addLocale(name, values);
            },
            removeLocale: function (name) {
                window.bootbox.removeLocale(name);
            },
            $get: function () {
                return ({
                    getDefaultLocale: function () {
                        return defaultLocale;
                    }
                });
            }
            /* eslint-enable angular/window-service */
        };
    }

    function AlertDirective(habilBootbox) {
        return {
            restrict: 'A',
            scope: false,
            link: function (_scope, element, attr) {
                const msg = attr.habilBootboxAlert || "Yo!";
                element.bind('click', function () {
                    habilBootbox.alert(msg);
                });
            }
        };
    }

    function ConfirmDirective(habilBootbox) {
        return {
            restrict: 'A',
            scope: {
                actionOK: '&habilBootboxConfirmAction',
                actionCancel: '&habilBootboxConfirmActionCancel'
            },
            link: function (scope, element, attr) {
                const msg = attr.habilBootboxConfirm || "Are you sure?";
                element.bind('click', function () {
                    habilBootbox.confirm(msg).then(scope.actionOK, scope.actionCancel);
                });
            }
        };
    }

    function PromptDirective(habilBootbox) {
        return {
            restrict: 'A',
            scope: {
                actionOK: '&habilBootboxPromptAction',
                actionCancel: '&habilBootboxPromptActionCancel',
                value: '@habilBootboxPromptDefaultValue',
                selectAllOnFocus: '@habilBootboxPromptSelectAllOnFocus'
            },
            link: Link
        };

        function Link(scope, element, attr) {
            const msg = attr.habilBootboxPrompt || "Are you sure?";
            const value = attr.habilBootboxPromptDefaultValue || "";
            const selectAllOnFocus = scope.$eval(attr.habilBootboxPromptSelectAllOnFocus) || false;
            element.bind('click', function () {
                habilBootbox.prompt(msg, value, selectAllOnFocus).then(onSubmit, scope.actionCancel);
            });

            function onSubmit(result) {
                scope.actionOK({ result: result });
            }
        }
    };

    function CustomDialogDirective(habilBootbox) {
        return {
            restrict: 'A',
            scope: {
                title: '@habilBootboxTitle',
                buttons: '=habilBootboxButtons',
                className: '@habilBootboxClassName',
                data: '=habilBootboxData',
                options: '=habilBootboxOptions'
            },
            link: function (scope, element, attr) {
                const options = NormalizeOptions(scope, attr);

                element.bind('click', function () {
                    habilBootbox.customDialog(options);
                });
            }
        };

        function NormalizeOptions(scope, attr) {
            let options = {};
            
            if (scope.options) {
                options = scope.options;
            }

            if (scope.title)
                options.title = scope.title;
            if (scope.buttons)
                options.buttons = scope.buttons;
            if (scope.className)
                options.className = scope.className;
            if (scope.data)
                options.data = scope.data;

            if (options.templateUrl || options.templateHtml)
                return options;

            const templateUrl = attr.habilBootboxCustomDialogTemplate;
            if (templateUrl) {
                options.templateUrl = templateUrl;
            } else {
                options.message = attr.habilBootboxCustomDialog;
            }
            return options;
        }
    }

    function CloseDialogDirective(habilBootbox) {
        return {
            restrict: 'A',
            link: function (_scope, element) {
                element.bind('click', function () {
                    habilBootbox.hideAll();
                });
            }
        };
    }

    function HabilBootboxService($q, $templateCache, $compile, $rootScope, $http, $window, $sce, translationsService) {
        return {
            alert: function (msg) {
                const deferred = $q.defer();
                function _callback() {
                    deferred.resolve();
                }

                let options = {};

                if (typeof (msg) === "object") {
                    options = { ...options, ...msg };
                }
                else {
                    // In this case we know we do not provide a title so also hide the closeButton
                    options.closeButton = false;
                    options.message = msg;
                }

                options = prepareContent(options);
                options.callback = _callback;

                if (options.templateUrl) {
                    showWithTemplateUrl(options, $window.bootbox.alert, false);
                } else {
                    $window.bootbox.alert(options);
                }

                return deferred.promise;
            },
            confirm: function (msg) {
                const deferred = $q.defer();
                function _callback(result) {
                    if (result) {
                        deferred.resolve();
                    }
                    else {
                        deferred.reject();
                    }
                }

                let options = { swapButtonOrder: true, callback: _callback };
                
                if (typeof (msg) === "object") {
                    options = { ...options, ...msg };
                }
                else {
                    // In this case we know we do not provide a title so also hide the closeButton
                    options.closeButton = false;
                    options.message = msg;
                }

                options = prepareContent(options);

                $window.bootbox.confirm(options);
                
                return deferred.promise;
            },
            prompt: function (options) {
                const deferred = $q.defer();

                options = options || {};
                options.swapButtonOrder = true;

                options.callback = function (result) {
                    if (result !== null) {
                        deferred.resolve(result);
                    }
                    else {
                        deferred.reject();
                    }
                };

                if (options.required) {
                    options.errorMessage = options.errorMessage || translationsService.GetTranslationByKey("Form_Error_required");
                }

                options = prepareContent(options);

                $window.bootbox.prompt(options);
                return deferred.promise;
            },
            customDialog: function (options) {
                if (options.templateUrl) {
                    showWithTemplateUrl(options, $window.bootbox.dialog, true);
                } else {
                    
                    options = prepareContent(options);

                    $window.bootbox.dialog(options);
                }
            },
            setDefaults: function (options) {
                $window.bootbox.setDefaults(options);
            },
            hideAll: function () {
                $window.bootbox.hideAll();
            },
            setLocale: function (name) {
                $window.bootbox.setLocale(name);
            },
            addLocale: function (name, values) {
                $window.bootbox.addLocale(name, values);
            },
            removeLocale: function (name) {
                $window.bootbox.removeLocale(name);
            }
        };

        function showWithTemplateUrl(options, dialog, compile) {
            if (!options.templateUrl)
                return;

            getTemplate(options.templateUrl, false)
                .then(
                    function (template) {
                        if (compile) {
                            options.scope = options.scope || $rootScope;
                            options.message = $compile(template)(options.scope);
                        }
                        else {
                            options.message = $sce.getTrustedHtml(template);
                        }
                        
                        dialog(options);
                    },
                    function () {
                        dialog(options);
                    }
                );
        }

        function getTemplate(templateId, ignoreCache) {
            const deferred = $q.defer();

            const template = $templateCache.get(templateId);
            if (typeof template === "undefined" || ignoreCache) {
                $http.get(templateId)
                    .then(function (response) {
                        const data = response.data;
                        $templateCache.put(templateId, data);
                        deferred.resolve(data);
                    });
            } else {
                deferred.resolve(template);
            }
            return deferred.promise;
        }

        function prepareContent(options) {
            if (!options || options.templateUrl)
                return options;


            if (options.templateHtml) {
                options.scope = options.scope || $rootScope;
                options.message = $compile(options.templateHtml)(options.scope);
            }
            else if (options.message) {
                options.message = $sce.getTrustedHtml(options.message);
            }

            return options;
        }
    }
})();